// ** React Imports
import { useState, Fragment, useRef } from 'react'

// ** MUI Imports
import Box from '@mui/material/Box'
import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import MuiMenu from '@mui/material/Menu'
import MuiMenuItem from '@mui/material/MenuItem'
import Typography from '@mui/material/Typography'

// ** Icon Imports
import Icon from 'src/@core/components/icon'

// ** Third Party Components
import PerfectScrollbarComponent from 'react-perfect-scrollbar'

// ** Custom Components Imports
import CustomChip from 'src/@core/components/mui/chip'

import { CircularProgress, Grid, ListItem, ListItemAvatar, ListItemText } from '@mui/material'
import Quantity from 'src/components/Quantity'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import CartServices from 'src/services/cart'
import { useAuth } from 'src/hooks/useAuth'
import { currencyFormat } from 'src/utility/Utils'
import Link from 'next/link'
import { grey } from '@mui/material/colors'

// ** Styled Menu component
const Menu = styled(MuiMenu)(({ theme }) => ({
  '& .MuiMenu-paper': {
    width: 380,
    overflow: 'hidden',
    marginTop: theme.spacing(4.5),
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  '& .MuiMenu-list': {
    padding: 0,
    '& .MuiMenuItem-root': {
      margin: 0,
      borderRadius: 0,
      padding: theme.spacing(4, 6),
      '&:hover': {
        backgroundColor: theme.palette.action.hover
      }
    }
  }
}))

// ** Styled MenuItem component
const MenuItem = styled(MuiMenuItem)(({ theme }) => ({
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(3),
  '&:not(:last-of-type)': {
    borderBottom: `1px solid ${theme.palette.divider}`
  }
}))

// ** Styled PerfectScrollbar component
const PerfectScrollbar = styled(PerfectScrollbarComponent)({
  maxHeight: 349
})

const ScrollWrapper = ({ children, hidden }) => {
  if (hidden) {
    return <Box sx={{ maxHeight: 349, overflowY: 'auto', overflowX: 'hidden' }}>{children}</Box>
  } else {
    return <PerfectScrollbar options={{ wheelPropagation: false, suppressScrollX: true }}>{children}</PerfectScrollbar>
  }
}

const NotificationDropdown = props => {
  // ** Props
  // const { settings, notifications } = props

  // ** States
  const [anchorEl, setAnchorEl] = useState(null)
  const activeCartItemRef = useRef(null)
  const queryClient = useQueryClient()
  const {isLogin} = useAuth()

  const { data, isLoading } = useQuery({
    enabled: isLogin,
    queryKey: ['cart'],
    queryFn: CartServices.fetchCart
  })

  const { mutate: addToCartMutate, isLoading: addToCartLoading } = useMutation(CartServices.addToCart, {
    onSuccess: data => {
      queryClient.setQueryData(['cart'], data)
    },
    onError: err => {},
    onSettled: () => {}
  })

  const { mutate: removeFromCartMutate, isLoading: removeFromCartLoading } = useMutation(CartServices.removeFromCart, {
    onSuccess: data => {
      queryClient.setQueryData(['cart'], data)
    },
    onError: err => {},
    onSettled: () => {}
  })

  // ** Hook
  const hidden = useMediaQuery(theme => theme.breakpoints.down('lg'))

  // ** Vars
  // const { direction } = settings

  const handleDropdownOpen = event => {
    setAnchorEl(event.currentTarget)
  }

  const handleDropdownClose = () => {
    setAnchorEl(null)
  }

  const handleAddToCart = product => {
    activeCartItemRef.current = product.id
    addToCartMutate(product)
  }

  const handleRemoveFromCart = product => {
    activeCartItemRef.current = product.id
    removeFromCartMutate(product)
  }

  return (
    <Fragment>
      <Button size='small' aria-haspopup='true' onClick={handleDropdownOpen} aria-controls='customized-menu'>
        <Badge
          color='primary'
          badgeContent={
            data?.data?.carts?.reduce((previousValue, currentValue) => previousValue + currentValue.quantity, 0) ?? 0
          }
          max={99}
          sx={{
            '& .MuiBadge-badge': {
              top: -4,
              right: 0,
              boxShadow: theme => `0 0 0 2px ${theme.palette.background.paper}`
            }
          }}
        >
          <Typography color={'black'} variant='caption1'>
            سبد خرید
          </Typography>
        </Badge>
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleDropdownClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem
          disableRipple
          disableTouchRipple
          sx={{ cursor: 'default', userSelect: 'auto', backgroundColor: 'transparent !important' }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <Typography sx={{ cursor: 'text', fontWeight: 600 }}>سبد خرید</Typography>
            <CustomChip
              skin='light'
              size='small'
              color='primary'
              label={
                data?.data?.carts?.reduce((previousValue, currentValue) => previousValue + currentValue.quantity, 0) ??
                0
              }
              sx={{ height: 20, fontSize: '0.75rem', fontWeight: 500, borderRadius: '10px' }}
            />
          </Box>
        </MenuItem>
        <ScrollWrapper hidden={hidden}>
          {data?.data?.carts?.map((item, index) => (
            <MenuItem key={item.id} disableRipple disableTouchRipple>
              <ListItem>
                <ListItemAvatar sx={{ mr: 5 }}>
                  <img width={50} height={60} style={{ objectFit: 'contain' }} src={item.image} alt={item.title} />
                </ListItemAvatar>
                <Box>
                  <Typography variant='subtitle2' maxWidth={10} width={10} textOverflow={'ellipsis'}>
                    {item.title}
                  </Typography>
                  <Grid container alignItems={'center'} mt={5}>
                    <Grid item xs={6}>
                      {(addToCartLoading || removeFromCartLoading) && activeCartItemRef.current === item.id ? (
                        <Box textAlign={'center'}>
                          <CircularProgress size={20} />
                        </Box>
                      ) : (
                        <Quantity
                          data={item}
                          quantity={item.quantity}
                          onDecrease={handleRemoveFromCart}
                          onIncrease={handleAddToCart}
                        />
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography
                        textAlign={'center'}
                        sx={{ fontSize: 16, color: 'primary.main', '& span': { ml: 1 } }}
                      >
                        {currencyFormat(item.total_price)}
                        <span>تومان</span>
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              </ListItem>
            </MenuItem>
          ))}
        </ScrollWrapper>
        <Box my={5} px={10} display={'flex'} alignItems={'center'} justifyContent={'space-between'}>
          <Typography variant='body2' color={grey[600]}>
            مجموع:
          </Typography>

          <Typography variant='body1' sx={{ fontWeight: 'bolder', '& span': { ml: 1 } }} color={'primary.main'}>
            {currencyFormat(data?.data?.total_price || 0)}

            <span>تومان</span>
          </Typography>
        </Box>
        <MenuItem
          disableRipple
          disableTouchRipple
          sx={{
            textAlign: 'center',
            borderBottom: 0,
            cursor: 'default',
            userSelect: 'auto',
            backgroundColor: 'transparent !important',
            borderTop: theme => `1px solid ${theme.palette.divider}`
          }}
        >
          {data?.data?.total_price ? (
            <Button LinkComponent={Link} href='/checkout' fullWidth variant='contained' onClick={handleDropdownClose}>
              ثبت سفارش
            </Button>
          ) : (
            <Typography mx={'auto'} textAlign={'center'} variant='body2'>
              سبد خرید شما خالی می باشد
            </Typography>
          )}
        </MenuItem>
      </Menu>
    </Fragment>
  )
}

export default NotificationDropdown
